import { createTheme, type Theme } from '@mui/material/styles'
import type {} from '@mui/x-data-grid/themeAugmentation'

/**
 * Class applied to every numeric grid column. Credits, counts and confidence
 * scores are read down a column and compared, so the digits must line up:
 * proportional figures make 1,024 look narrower than 999.
 */
export const NUMERIC_CELL = 'numeric-cell'

/**
 * The HabileLabs blues, taken from the official mark
 * (habilelabs_logo_single_b2637a3061.svg, which the company site serves as its
 * own favicon) rather than sampled or guessed. The mark is #2eaee9 with a
 * gradient running #0b62a2 to #1bb9e9.
 *
 * The mark blue is only 2.5:1 on white, so it carries identity and accents but
 * never text or a button label. Interactive colour on light uses the gradient's
 * deep end, #0b62a2, at 6.4:1. Dark mode inverts it: #0b62a2 is 2.9:1 on our
 * near-black ground, where the mark blue reaches 7.4:1.
 */
export const BRAND = {
  /** The mark. Identity and accents only, never text on white. */
  mark: '#2eaee9',
  light: '#1bb9e9',
  deep: '#0b62a2',
} as const

const BRAND_LIGHT = {
  main: BRAND.deep,
  light: BRAND.mark,
  dark: '#084B7C',
} as const

const BRAND_DARK = {
  main: BRAND.mark,
  light: '#6FCBF2',
  dark: BRAND.deep,
} as const

export function buildTheme(mode: 'light' | 'dark'): Theme {
  const dark = mode === 'dark'

  return createTheme({
    palette: {
      mode,
      primary: dark ? BRAND_DARK : BRAND_LIGHT,
      secondary: { main: dark ? '#9AA9B2' : '#4A5C66' },
      success: { main: dark ? '#4CAF7D' : '#1E7A4D' },
      /*
       * Amber is read here, not just noticed: "unverified freemail", "needs
       * review", "name match" are 12px chip labels and captions in this colour,
       * and they are the signals somebody is meant to catch before they send.
       * The previous #A96B0B came to 4.38:1 on white and 4.04:1 on the page
       * ground — under the 4.5:1 AA floor for text this size. #8A5600 is a
       * duller amber on purpose: 6.1:1 and 5.7:1. Dark mode already passes.
       */
      warning: { main: dark ? '#E0A94A' : '#8A5600' },
      error: { main: dark ? '#E57373' : '#B3261E' },
      info: { main: dark ? BRAND_DARK.main : BRAND_LIGHT.main },
      background: {
        default: dark ? '#0E1418' : '#F4F6F7',
        paper: dark ? '#151D22' : '#FFFFFF',
      },
      text: {
        primary: dark ? '#E3E8EB' : '#182126',
        secondary: dark ? '#9DAAB2' : '#55666F',
      },
      divider: dark ? 'rgba(157,170,178,0.20)' : 'rgba(24,33,38,0.14)',
    },
    shape: { borderRadius: 6 },
    typography: {
      fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      h5: { fontSize: '1.25rem', fontWeight: 600, letterSpacing: '-0.01em' },
      h6: { fontSize: '1.0625rem', fontWeight: 600, letterSpacing: '-0.005em' },
      subtitle2: { fontWeight: 600 },
      body2: { fontSize: '0.875rem' },
      button: { textTransform: 'none', fontWeight: 500 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          // Long LinkedIn URLs and titles otherwise force the whole page sideways.
          'code, .mono': {
            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
            fontSize: '0.8125rem',
          },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'default' },
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: theme.palette.background.paper,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }),
        },
      },
      MuiPaper: { defaultProps: { elevation: 0 }, styleOverrides: { root: { backgroundImage: 'none' } } },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: ({ theme }) => ({ border: `1px solid ${theme.palette.divider}` }),
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 500 },
          sizeSmall: { height: 22, fontSize: '0.75rem' },
        },
      },
      MuiTooltip: { defaultProps: { arrow: true } },
      MuiTextField: { defaultProps: { size: 'small' } },
      MuiSelect: { defaultProps: { size: 'small' } },
      MuiButton: { defaultProps: { disableElevation: true } },
      MuiTableCell: { styleOverrides: { root: { fontSize: '0.8125rem' } } },
      MuiDataGrid: {
        styleOverrides: {
          root: ({ theme }) => ({
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            '--DataGrid-overlayHeight': '260px',
            [`& .${NUMERIC_CELL}`]: {
              fontVariantNumeric: 'tabular-nums',
              fontFeatureSettings: '"tnum" 1',
            },
            '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600 },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outlineOffset: -2 },
          }),
        },
      },
    },
  })
}
